import { DBSERVICE_START_URL, THEME_HOST_URL } from "@/store";
import { AjaxURLForward, DBServiceAjaxData, DBServiceData, DBServiceField, DBServiceFieldArea, DBServiceScriptsData, DBServiceStep, FormDataModel, MainStateData, SubmitStepAttrs, ThemeData, ValidateErrorObject, ValidateErrors } from "@/types";

let versionInfo = "";
if (process.env.NODE_ENV === "production") {
	versionInfo = process.env.APP_VERSION;
} else {
	versionInfo = process.env.APP_VERSION_DEV;
}


const state: MainStateData = {
	_appVersion: versionInfo,
	requestError: "",
	loadingControllers: [],
	loadingData: "",
	loadingDialogPresent: false,
	submitStepAttrs: null,
	ajaxItems: [],
	stepId: "",
	stepData: null,
	themeData: null,
	scriptsData: null,
	stepPayload: null,
	startValidate: false,
	validateErrors: {},
	formData: {},
	fullheight: false,
	forceKeyboard: false,
	focussedFieldname: "",
	keyboard: null,
	bodyClickEventTargetNodeName: "",
};

const getters = {
	getRequestError: (state: any): string => { return state.requestError; },
	getAppVersion: (state: any): string => { return state._appVersion; },
	getLoadingData: (state: any): string => { return state.loadingData; },
	getLoadingControllers: (state: any): string => { return state.loadingControllers; },
	getLoadingDialogPresent: (state: any): boolean => { return state.loadingDialogPresent; },
	getSubmitStepAttrs: (state: any): SubmitStepAttrs | null => { return state.submitStepAttrs; },
	getStepId: (state: any): string => { return state.stepId; },
	getStepData: (state: any): DBServiceData => { return state.stepData; },
	getThemeData: (state: any): ThemeData => { return state.themeData; },
	getScriptsData: (state: any): DBServiceScriptsData[] | null => { return state.scriptsData; },
	getStepPayload: (state: any): any => { return state.stepPayload; },
	getStepDataItems: (state: any): any => {
		if (state.stepData && state.stepData.items) {
			return state.stepData.items;
		}
		return null;
	},
	getAjaxItems: (state: any): DBServiceAjaxData[] => { return state.ajaxItems; },
	getStartValidate: (state: any): boolean => { return state.startValidate; },
	getValidateErrors: (state: any): ValidateErrors => { return state.validateErrors; },
	getFormData: (state: any): FormDataModel => { return state.formData; },
	getPostBodyForRequest: (state: any, getters: any): string => {
		const step: DBServiceStep = {
			id: getters.getStepId,
			payload: getters.getStepPayload,
			data: null
		};
		const stepData = { ...getters.getStepData } as DBServiceData;
		if (stepData) {
			const formData: FormDataModel = getters.getFormData;
			if (Object.keys(formData).length) {
				const realItems = []
				for (const item of stepData.items) {
					const name = item.name as string || undefined;
					if (name && formData[name]) {
						realItems.push(formData[name]);
					} else {
						realItems.push(item);
					}
				}
				stepData.items = realItems;
			}
			step.data = stepData;
			return JSON.stringify(step);
		}
		return "";
	},
	getFullheight: (state: any): boolean => { return state.fullheight; },
	getForceKeyboard: (state: any): boolean => { return state.forceKeyboard; },
	getFocussedFieldname: (state: any): string => { return state.focussedFieldname; },
	getFocussedFieldValue: (state: any): string => {
		if (state.formData[state.focussedFieldname]) {
			return state.formData[state.focussedFieldname].value;
		}
		return "";
	},
	getKeyboard: (state: any): any => { return state.keyboard; },
	getBodyClickEventTargetNodeName: (state: any): string => { return state.bodyClickEventTargetNodeName; },
};

const mutations = {
	setRequestError: (state: any, error: string) => { state.requestError = error; },
	setLoadingData: (state: any, message?: string) => {
		if (message) {
			state.loadingData = message;
		} else {
			state.loadingData = "";
		}
	},
	setLoadingControllers: (state: any, controller?: any) => {
		if (controller) {
			state.loadingControllers.push(controller);
			state.loadingDialogPresent = true;
		} else {
			state.loadingControllers = [];
			state.loadingDialogPresent = false;
		}
	},
	setLoadingDialogPresent: (state: any, present: boolean) => { state.loadingDialogPresent = present; },
	setSubmitStepAttrs: (state: any, attrs: SubmitStepAttrs | null) => { state.submitStepAttrs = attrs; },
	setStepId: (state: any, stepId: string) => {
		state.stepId = stepId;
	},
	setStepData: (state: any, step: DBServiceData) => {
		state.stepData = step;
	},
	setThemeData: (state: any, theme: ThemeData) => { state.themeData = theme; },
	setScriptsData: (state: any, scripts?: DBServiceScriptsData[]) => {
		if (scripts && scripts.length) {
			state.scriptsData = scripts;
		} else {
			state.scriptsData = null;
		}
	},
	setStepPayload: (state: any, payload: any) => {
		state.stepPayload = payload;
	},
	setStepDataItems: (state: any, items: any) => {
		if (state.stepData) {
			state.stepData.items = items;
		}
	},
	setAjaxItems: (state: any, data: DBServiceAjaxData[]) => {
		if (data) {
			state.ajaxItems = data;
		} else {
			const ajaxItems = state.ajaxItems as DBServiceField[] | DBServiceFieldArea[];
			for (const ajaxItem of ajaxItems) {
				if (ajaxItem.ajax) {
					clearInterval(ajaxItem.ajax.__intervalref);
					if (ajaxItem.ajax.__signal) {
						ajaxItem.ajax.__signal.abort();
					}
				}
			}
			state.ajaxItems = [];
		}
	},
	setStartValidate: (state: any, validate: boolean) => { state.startValidate = validate; },
	setValidateErrors: (state: any, validateErrors: ValidateErrors) => { state.validateErrors = validateErrors; },
	setFormData: (state: any, attrs?: any) => {
		if (attrs) {
			state.formData[attrs.key] = attrs.field;
		} else {
			state.formData = {};
		}
	},
	setFullheight: (state: any, value: boolean) => { state.fullheight = value; },
	setForceKeyboard: (state: any, value?: boolean) => { state.forceKeyboard = value || false; },
	setFocussedFieldname: (state: any, value?: string) => { state.focussedFieldname = value || ""; },
	setFocussedFieldValue: (state: any, value?: string) => {
		if (state.formData[state.focussedFieldname]) {
			state.formData[state.focussedFieldname].value = value || "";
		}
	},
	setInitialFieldErrors: (state: any) => {
		const stepData = state.stepData as DBServiceData
		if (stepData && stepData.items) {
			for (const item of stepData.items) {
				if (["string", "lookup", "checkbox"].includes(item.type) && item.initerrormsg) {
					const error: ValidateErrorObject = { error: item.initerrormsg as string, field: item as DBServiceField }
					let valErrors = { ...(state.validateErrors as ValidateErrors) };
					if (!valErrors) {
						valErrors = {};
					}
					if (error.error) {
						valErrors[error.field.name] = error;
					} else {
						delete valErrors[error.field.name];
					}
					state.validateErrors = valErrors;
				}
			}
		}
	},
	setKeyboard: (state: any, keyboard?: any) => { state.keyboard = keyboard || null; },
	setBodyClickEventTargetNodeName: (state: any, target?: string) => { state.bodyClickEventTargetNodeName = target || ""; },
};

const actions = {
	setLoadingStatus: async ({ commit }: any, status: boolean) => {
		if (status) {
			commit("setLoadingData", status);
		} else {
			setTimeout(() => {
				commit("setLoadingData", status);
			}, 1000);
		}
	},
	updateStepData: async ({ commit, getters, dispatch }: any, stepUrl: string) => {
		await dispatch("updateThemeData");
		const url = stepUrl ? stepUrl : DBSERVICE_START_URL;
		const postBody = getters.getPostBodyForRequest;
		const resp: Response | void = await fetch(url, {
			method: "POST",
			headers: {
				"Accept": "application/json, text/plain, */*",
				"Content-Type": "application/json"
			},
			body: postBody
		}).catch((status) => {
			commit("setLoadingData");
			const message = `Not able to request step data: ${status}`;
			throw new Error(message);
		});
		if (resp && resp.ok) {
			// We dont want to create an error if no json response was here.
			// We want to catch it later by JSON.parse.
			const dataTxt = await resp.text();
			try {
				const data = JSON.parse(dataTxt) as DBServiceStep;
				// Only clear form data if the step id is different!
				if (getters.getStepId !== data.id) {
					commit("setFormData");
				}
				dispatch("updateScriptsData", data.scripts);
				commit("setAjaxItems");
				commit("setStepId", data.id);
				commit("setStepData", data.data);
				commit("setForceKeyboard");
				commit("setFocussedFieldname");
				commit("setInitialFieldErrors");
				if (data.payload) {
					commit("setStepPayload", data.payload);
				}
				if (data.forcekeyboard) {
					commit("setForceKeyboard", true);
				}
				commit("setBodyClickEventTargetNodeName");
				await dispatch("handleItemAjax", data.data);
			} catch (error) {
				const errmsg = `Invalid JSON-Response in JSON.parse: ${error}`;
				throw new Error(errmsg);
			} finally {
				commit("setLoadingData");
			}
		} else {
			commit("setLoadingData");
			const message = `${resp.status}, ${resp.statusText}`;
			throw new Error(message);
		}
		await commit("setSubmitStepAttrs", null);
	},
	updateThemeData: async ({ commit, getters }: any) => {
		const themeData = getters.getThemeData;
		if (!themeData) {
			const url = THEME_HOST_URL;
			const resp: Response | void = await fetch(url)
				.catch((status) => {
					const message = `Not able to request theme data: ${status}`;
					throw new Error(message);
				});
			if (resp && resp.ok) {
				const data: ThemeData | null = await resp.json();
				commit("setThemeData", data);
				if (data && data.css) {
					const style = document.createElement("style");
					document.head.append(style);
					style.textContent = data.css;
				}
			} else {
				const message = `${resp.status}, ${resp.statusText}`;
				throw new Error(message);
			}
		}
	},
	updateScriptsData: async ({ commit, getters }: any, scripts: DBServiceScriptsData[] | undefined) => {
		const exScripts = getters.getScriptsData as DBServiceScriptsData[] | null || [];
		const exScriptNames = [];
		if (exScripts && exScripts.length) {
			for (const esc of exScripts) {
				exScriptNames.push(esc.id);
			}
		}
		if (scripts && scripts.length) {
			for (const sc of scripts) {
				if (!exScriptNames.includes(sc.id)) {
					const script = document.createElement("script");
					const body = document.body || document.getElementsByTagName("body")[0];
					script.async = false;
					script.src = sc.url;
					script.id = sc.id;
					body.appendChild(script);
				}
			}
			commit("setScriptsData", [...exScripts, ...scripts]);
		}
	},
	countErrors: ({ getters }: any) => {
		const valErrors = getters.getValidateErrors as ValidateErrors;
		return Object.keys(valErrors).length;
	},
	submitStep: async ({ commit, dispatch }: any, attrs: SubmitStepAttrs) => {
		commit("setLoadingData");
		if (attrs.button && attrs.button === "cancel") {
			window.location.href = attrs.url;
		} else {
			if (attrs.doSubmit) {
				await dispatch("clearErrors");
				if (attrs.button && attrs.button !== "back") {
					await dispatch("startValidate");
				}
			}
			commit("setSubmitStepAttrs", attrs);
		}
	},
	clearErrors: ({ commit }: any) => {
		commit("setStartValidate", false);
		commit("setValidateErrors", {});
	},
	startValidate: ({ commit }: any) => {
		commit("setStartValidate", true);
	},
	handleValidatingError: ({ commit, getters }: any, error: ValidateErrorObject) => {
		let valErrors = { ...(getters.getValidateErrors as ValidateErrors) };
		if (!valErrors) {
			valErrors = {};
		}
		if (error.error) {
			valErrors[error.field.name] = error;
		} else {
			delete valErrors[error.field.name];
		}
		commit("setValidateErrors", valErrors);
	},
	handleFieldValue: ({ commit, getters }: any, attrs: any) => {
		const formData = getters.getFormData as FormDataModel;
		const fieldData = attrs.data as DBServiceField;
		if (fieldData.name) {
			const value = attrs.value;
			const field = { ...formData[fieldData.name] };
			if (fieldData.type === "checkbox") {
				field.checked = value;
			} else {
				field.value = value;
			}
			const setAttrs = {
				key: fieldData.name,
				field
			};
			commit("setFormData", setAttrs);
		}
	},
	handleItemAjax: ({ commit, getters, dispatch }: any, stepData: DBServiceData) => {
		if (stepData.items) {
			const ajaxItems = getters.getAjaxItems as DBServiceField[] | DBServiceFieldArea[];
			const realAjaxItems = [];
			for (const item of stepData.items) {
				let found = false;
				for (const ajaxItem of ajaxItems) {
					if (ajaxItem.pos === item.pos) {
						found = true;
						realAjaxItems.push(item);
					}
				}
				if (!found && item.ajax) {
					const ajaxdata = item.ajax as DBServiceAjaxData;
					realAjaxItems.push(item);
					if (ajaxdata.interval) {
						ajaxdata.__intervalref = setInterval(async () => {
							const url = ajaxdata.url;
							const postBody = getters.getPostBodyForRequest;
							if (ajaxdata.__signal) {
								ajaxdata.__signal.abort()
							}
							const abortController = new AbortController();
							ajaxdata.__signal = abortController;
							const resp: Response | void = await fetch(url, {
								method: "POST",
								headers: {
									"Accept": "application/json, text/plain, */*",
									"Content-Type": "application/json"
								},
								body: postBody,
								signal: abortController.signal,
							}).catch((status) => {
								if (status.name !== "AbortError") {
									const message = `Not able to request AJAX(interval) target: ${status}`;
									throw new Error(message);
								}
							});
							if (resp && resp.ok) {
								const dataTxt = await resp.text();
								try {
									const data = JSON.parse(dataTxt) as DBServiceStep;
									if (data && data.url || data.items) {
										await dispatch("updateItemsPerAjax", data);
									}
								} catch (error) {
									const errmsg = `Invalid JSON-Response in JSON.parse: ${error}`;
									throw new Error(errmsg);
								} finally {
									commit("setLoadingData");
								}
							} else if (resp) {
								const message = `${resp.status}, ${resp.statusText}`;
								throw new Error(message);
							}
						}, ajaxdata.interval);
					}
				}
			}
			commit("setAjaxItems", realAjaxItems);
		}
	},
	updateItemsPerAjax: async ({ commit, getters, dispatch }: any, data: AjaxURLForward) => {
		if (data.url) {
			await dispatch("updateStepData", data.url);
		} else if (data.items) {
			const stepData = (getters.getStepData as DBServiceData);
			const formData = getters.getFormData as FormDataModel;
			const items = stepData.items;
			const newItems: any = [];
			for (const item of items) {
				let found = false;
				for (const ajaxItem of data.items) {
					if (item.pos === ajaxItem.pos) {
						newItems.push(ajaxItem);
						if (ajaxItem.name) {
							for (const name in formData) {
								if (ajaxItem.name === name) {
									const formDataAttrs: any = {
										key: name,
										field: ajaxItem
									};
									commit("setFormData", formDataAttrs);
								}
							}
						}
						found = true;
					}
				}
				if (!found) {
					newItems.push(item);
				}
				commit("setLoadingData");
			}
			commit("setStepDataItems", newItems);
		}
	}
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions
};